import { FC, useMemo } from 'react';

import {
    Box,
    Button,
    CloseButton,
    Drawer,
    Link,
    Portal,
    Separator,
    Text,
    useSlotRecipe,
} from '@chakra-ui/react';

import { differenceInDays } from 'date-fns/differenceInDays';
import isNil from 'lodash/isNil';

import { dateHelpers } from '@nocowanie/core/helpers';
import { IconsLinear } from '@nocowanie/icons/linear';

import { RangePickerDrawerProps } from './range-picker-drawer.props';
import { RangePickerFlexDates } from './range-picker-flex-dates';

import { wordPluralisation } from '../../../helpers/words-pluralisation';

export const RangePickerDrawer = ({
    translationData,
    onOpenChange,
    onSubmit,
    open = true,
    startDate,
    endDate,
    displayedDateFormat = 'dd MMM.',
    footerContent,
    setIsLoading,
    isLoading,
    onClear,
    isClearable = false,
    withFlexDates = false,
    onFlexDateSelect,
    selectedFlexDays,
    children,
}: RangePickerDrawerProps) => {
    const DisplayedValue: FC = () => {
        const nightsLabel = useMemo(() => {
            if (!startDate || !endDate) {
                return;
            }

            const nightsCount = differenceInDays(endDate, startDate);

            return `${nightsCount} ${wordPluralisation(
                Number(nightsCount),
                translationData.nights.singular,
                translationData.nights.plural,
                translationData.nights.genitivePlural,
            )}`;
        }, [startDate, endDate]);

        const getLabelValue = (date: Date | null | undefined, placeholderExtension: string) =>
            date ? (
                <strong>{dateHelpers.format(date, displayedDateFormat)}</strong>
            ) : (
                <>
                    {translationData.datePlaceholder} <strong>{placeholderExtension}</strong>
                </>
            );

        const dateFrom = getLabelValue(startDate, translationData.dateFromPlaceholder);

        if (isNil(startDate) && isNil(endDate))
            return (
                <Text as={'span'} fontSize={'sm'}>
                    {dateFrom}
                </Text>
            );

        const dateTo = getLabelValue(endDate, translationData.dateToPlaceholder);

        return (
            <Text as={'span'} fontSize={'sm'}>
                {dateFrom} - {dateTo} {nightsLabel ? `(${nightsLabel})` : null}
            </Text>
        );
    };

    const renderWeekDayNames = useMemo(() => {
        const [first, ...rest] = [...translationData.weekDayNames];
        const weekDayNames: Array<string> = [...rest, first];

        return weekDayNames?.map(day => (
            <Text as="span" key={day} className="react-datepicker__day-name">
                {day}
            </Text>
        ));
    }, [translationData]);

    const recipe = useSlotRecipe({
        key: 'rangePickerDrawer',
    });
    const styles = recipe();

    const handleSubmit = () => {
        if (
            (!startDate && endDate) ||
            (startDate && !endDate) ||
            (!isClearable && !startDate && !endDate)
        ) {
            return;
        }

        setIsLoading?.(true);
        'requestIdleCallback' in window ? requestIdleCallback(() => onSubmit?.()) : onSubmit?.();
    };

    const showClearButton = isClearable && (startDate || endDate);

    return (
        <Drawer.Root open={open} placement="bottom" onOpenChange={onOpenChange} variant={'overlap'}>
            <Portal>
                <Drawer.Backdrop />
                <Drawer.Positioner>
                    <Drawer.Content pt={8} className={'wc-range-picker-drawer'}>
                        <Drawer.CloseTrigger asChild top={0.5} right={0.5}>
                            <CloseButton>
                                <IconsLinear.Close />
                            </CloseButton>
                        </Drawer.CloseTrigger>

                        {showClearButton ? (
                            <Link
                                as={'button'}
                                onClick={onClear}
                                fontSize={'md'}
                                css={{
                                    position: 'absolute',
                                    left: 6,
                                    top: 3.5,
                                }}
                            >
                                {translationData.clearLabel}
                            </Link>
                        ) : null}

                        {renderWeekDayNames ? (
                            <Drawer.Header
                                fontSize="md"
                                borderBottomWidth={1}
                                flexDirection={'column'}
                                gap={0}
                                pb={2}
                                mb={2}
                            >
                                <Separator
                                    width={'100vw'}
                                    borderColor={'{colors.border}'}
                                    opacity={1}
                                />
                                <Box className="react-datepicker--drawer" p={'0 0.5rem'}>
                                    <Box className="react-datepicker__day-names">
                                        {renderWeekDayNames}
                                    </Box>
                                </Box>
                            </Drawer.Header>
                        ) : null}
                        <Drawer.Body
                            pt={0}
                            pb={3}
                            px={6}
                            display={'flex'}
                            flexDirection={'column'}
                            overflow={'hidden'}
                        >
                            {children}
                        </Drawer.Body>
                        <Drawer.Footer
                            flexDirection={'column'}
                            gap={4}
                            borderTopWidth={1}
                            mt={0}
                            pt={3}
                            css={styles.footerContent}
                        >
                            {withFlexDates ? (
                                <RangePickerFlexDates
                                    translationData={translationData}
                                    onFlexDateSelect={onFlexDateSelect}
                                    selectedFlexDays={selectedFlexDays}
                                    withDrawer
                                />
                            ) : null}
                            <Box>{footerContent ?? <DisplayedValue />}</Box>
                            <Button
                                css={styles.button}
                                variant={'solid'}
                                onClick={handleSubmit}
                                loading={isLoading && !!(startDate && endDate)}
                                disabled={!!(startDate && !endDate)}
                            >
                                {translationData.submitAction}
                            </Button>
                        </Drawer.Footer>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    );
};
